/*
 * Industry content — `it` (Italian, translated from `en` source locale).
 *
 * Italian variant of every industry: generic time-registration framing, no
 * belgium-specific pains/features, and `compliance.slug` / `relatedArticles`
 * pointing only at articles that exist under `/it/` (the 4 market-agnostic
 * posts). Resolved by `industriesFor("it")`. Pure data (NFR10).
 */
import type { Industry } from "./types";

export const INDUSTRIES_IT: Industry[] = [
  {
    slug: "construction",
    name: "Edilizia",
    headline: "Rilevazione delle ore per i team edili — check-in con verifica della posizione, ore di progetto e controllo del budget, in un'unica app",
    intro: "Squadre distribuite tra i cantieri, ore scritte a mano su carta, ore dei subappaltatori impossibili da riconciliare. Done-it dà a ogni cantiere un foglio ore digitale a prova di tutto che alimenta le tue buste paga con un clic.",
    pains: [
      {
        title: "Nessun record affidabile di chi ha lavorato dove",
        body: "Con più cantieri attivi e squadre a rotazione, i fogli ore manuali sono sbagliati per natura. I lavoratori arrotondano, dimenticano le registrazioni o le compilano giorni dopo — lasciandoti cifre per le buste paga che non puoi difendere.",
      },
      {
        title: "Gli sforamenti di budget emergono troppo tardi",
        body: "Quando ti accorgi che un cantiere ha esaurito le ore previste, il margine è già perso. Non c'è un conto alla rovescia in tempo reale — solo una brutta sorpresa al momento della fattura.",
      },
      {
        title: "La preparazione delle buste paga divora ore ogni mese",
        body: "Raccogliere i fogli ore, riconciliarli con i progetti e preparare l'esportazione per le buste paga richiede giorni. Ogni registrazione è un potenziale errore.",
      },
    ],
    features: [
      {
        title: "Check-in con verifica della posizione per ogni cantiere",
        body: "Un tocco registra l'entrata del lavoratore, con posizione verificata, e collega le ore al progetto giusto. Niente lettori di tessere, niente terminali — solo lo smartphone che la tua squadra ha già in tasca.",
      },
      {
        title: "Ore residue in tempo reale per progetto",
        body: "Imposta le ore previste come budget quando apri il progetto. I lavoratori vedono un conto alla rovescia di ciò che resta; tu lo vedi in tempo reale nella tua dashboard. Gli sforamenti di budget sono visibili prima che accadano.",
      },
      {
        title: "Gestione e presenza dei subappaltatori",
        body: "Aggiungi i subappaltatori a un progetto, assegnali al check-in e traccia le loro ore insieme alla tua squadra — nella stessa panoramica.",
      },
      {
        title: "Esportazione per le buste paga con un clic",
        body: "Tutte le ore registrate — per lavoratore, per progetto — si esportano in un foglio di calcolo pulito per il tuo fornitore di buste paga. Nessuna preparazione manuale, nessun errore.",
      },
    ],
    compliance: {
      label: "Registrazione delle ore digitale",
      title: "Check-in con verifica della posizione pensato per i cantieri edili",
      body: "Done-it fornisce check-in con verifica della posizione, rilevazione delle ore per progetto e un'esportazione pulita per le buste paga dei team edili. Per i team che operano nei cantieri belgi, alimenta anche automaticamente check-in at work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "La squadra arriva in cantiere", body: "Ogni lavoratore tocca una volta per registrare l'entrata. Il GPS conferma la posizione, marca temporalmente la registrazione e collega le ore al progetto. Niente carta, niente terminale." },
      { step: "Vedi tutto in tempo reale", body: "Ore, budget residuo, presenza dei subappaltatori ed eventuali lavori extra segnalati dal cantiere — tutto nella tua dashboard, in tempo reale." },
      { step: "Fine mese in pochi minuti", body: "Esporta tutte le ore per lavoratore e per progetto. Invia al tuo fornitore di buste paga. Done-it genera automaticamente report puliti e pronti per l'audit." },
    ],
    stats: [
      { value: "1 tocco", label: "per registrare l'entrata in qualsiasi cantiere — niente terminale, niente carta" },
      { value: "1 clic", label: "esportazione per le buste paga pronta per il tuo fornitore" },
      { value: "In tempo reale", label: "vista delle ore residue per progetto e cantiere" },
    ],
    quote: {
      text: "La nostra squadra registra l'entrata da sola in cantiere. A fine mese, le buste paga semplicemente tornano — abbiamo smesso del tutto di rincorrere i fogli ore.",
      who: "Titolare, impresa edile · 14 dipendenti",
      result: "5 ore a settimana risparmiate sul lavoro amministrativo",
    },
    relatedSlugs: ["installation", "maintenance", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "cleaning",
    name: "Pulizie",
    headline: "Rilevazione delle ore per le imprese di pulizie — check-in con verifica della posizione, prova di presenza e buste paga in un'unica app",
    intro: "Gli addetti alle pulizie si spostano tra le sedi tutto il giorno. Done-it traccia in tempo reale chi è dove — check-in con verifica della posizione in ogni sede, detrazione automatica delle pause e un'esportazione pulita per le buste paga a fine mese.",
    pains: [
      {
        title: "Dimostrare la presenza in ogni sede",
        body: "Clienti e ispettori si aspettano registrazioni delle presenze a prova di tutto. I fogli manuali possono essere contestati. Ogni registrazione ha la posizione verificata.",
      },
      {
        title: "Squadre distribuite su molte sedi",
        body: "Con gli addetti che si spostano tra le sedi durante la giornata, non hai una vista in tempo reale di chi lavora dove — finché qualcuno non chiama.",
      },
      {
        title: "Errori nelle buste paga dovuti alle ore arrotondate",
        body: "Senza orari di entrata/uscita accurati, le buste paga vengono ricostruite a memoria. Le ore vengono arrotondate per eccesso, i costi si gonfiano e seguono le contestazioni.",
      },
    ],
    features: [
      {
        title: "Check-in ed check-out in tempo reale",
        body: "I lavoratori timbrano entrata e uscita sul telefono in ogni sede, con posizione verificata; il tempo è esatto, con la registrazione delle pause.",
        plan: "Time Basic",
      },
      {
        title: "Detrazione automatica delle pause",
        body: "Le pause obbligatorie vengono sottratte automaticamente dalla giornata lavorativa — niente calcoli manuali, niente contestazioni.",
        plan: "Time Basic",
      },
      {
        title: "Panoramica multi-sede in tempo reale",
        body: "Vedi quali addetti sono in quali sedi in questo momento. Individua le lacune prima dei clienti.",
      },
      {
        title: "Registrazione delle assenze con certificati medici digitali",
        body: "I lavoratori caricano certificati di malattia e documenti di assenza direttamente dall'app. Crea i tipi di assenza che la tua squadra usa davvero.",
      },
      {
        title: "Esportazione per le buste paga con un clic",
        body: "Le marche temporali esatte confluiscono direttamente in un'esportazione pronta per le buste paga del tuo fornitore. Niente trascrizione, niente errori.",
      },
    ],
    compliance: {
      label: "Presenze con verifica della posizione",
      title: "Presenze in tempo reale in ogni sede di pulizia",
      body: "Done-it dà ai team di pulizie il check-in con verifica della posizione in ogni sede, la detrazione automatica delle pause e una vista in tempo reale di chi è in quale cantiere in questo momento.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "L'addetto arriva alla sede", body: "Un tocco registra l'entrata. Il GPS registra la sede e marca temporalmente la registrazione. La registrazione delle pause è integrata nell'app." },
      { step: "Vedi ogni sede in tempo reale", body: "Chi è in quale sede in questo momento — niente telefonate, niente supposizioni. Individua le lacune di copertura prima dei clienti." },
      { step: "Le buste paga sono un clic", body: "Tutti i dati di presenza — orari esatti, sedi e pause — si esportano in modo pulito per il tuo fornitore di buste paga." },
    ],
    stats: [
      { value: "1 tocco", label: "per registrare l'entrata in qualsiasi sede — nessun terminale necessario" },
      { value: "In tempo reale", label: "vista di chi è in quale sede in questo momento" },
      { value: "0", label: "doppie registrazioni — le ore confluiscono direttamente nelle buste paga" },
    ],
    quote: {
      text: "Possiamo finalmente dimostrare chi ha pulito dove e quando. Niente più fogli presenze, niente più contestazioni dei clienti — ci pensa il GPS.",
      who: "Responsabile, impresa di pulizie · 9 dipendenti",
      result: "Zero arrotondamenti delle ore",
    },
    relatedSlugs: ["maintenance", "field-service", "construction"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "installation",
    name: "Installazione",
    headline: "Rilevazione delle ore per i team di installazione — ore di progetto, navigazione e controllo del budget in un'unica app",
    intro: "Manda i tuoi installatori all'indirizzo giusto con tutto ciò che serve, collega automaticamente ogni ora lavorata al lavoro giusto e vedi il budget residuo prima che il cantiere chiuda. Done-it è il modo più semplice per tenere i progetti di installazione nei tempi e nei costi.",
    pains: [
      {
        title: "Le informazioni sul lavoro non arrivano sul campo in tempo",
        body: "Le squadre arrivano in un cantiere senza l'indirizzo, la persona di contatto o i dettagli dell'ambito. Quella prima telefonata sono dieci minuti di tempo fatturabile persi prima ancora di lavorare un'ora.",
      },
      {
        title: "Le ore non sono collegate al progetto giusto",
        body: "Quando i lavoratori compilano le ore manualmente a fine giornata — se le compilano — le ore finiscono nel lavoro sbagliato, o spariscono del tutto. Le fatture non corrispondono alla realtà.",
      },
      {
        title: "Gli sforamenti di budget vengono individuati troppo tardi",
        body: "Senza un conto alla rovescia in tempo reale delle ore residue per progetto, scopri che un lavoro è andato fuori budget quando chiudi la fattura — non mentre puoi ancora reagire.",
      },
      {
        title: "Difficile tracciare le ore dei subappaltatori",
        body: "I subappaltatori che lavorano sullo stesso cantiere usano sistemi diversi, compilano le ore separatamente o non le compilano affatto. Riconciliare a fine mese è un processo lento e soggetto a errori.",
      },
    ],
    features: [
      {
        title: "Informazioni di progetto condivise direttamente sull'app",
        body: "I lavoratori aprono il progetto sul telefono: indirizzo, ore previste, persona di contatto e file caricati (piani, istruzioni, PDF). Tutto ciò che serve è lì prima ancora di arrivare.",
        plan: "Project Basic",
      },
      {
        title: "Navigazione verso il cantiere con un solo pulsante",
        body: "Tocca una volta e il telefono naviga verso l'indirizzo del progetto — integrato nell'app, senza bisogno di un'app di mappe separata.",
      },
      {
        title: "Conto alla rovescia delle ore residue per progetto",
        body: "Imposta le ore previste quando apri il progetto. I lavoratori vedono quante ore restano; tu vedi lo stato in tempo reale nella tua dashboard. Gli sforamenti sono visibili prima che accadano.",
        plan: "Project Basic",
      },
      {
        title: "Check-in del lavoratore per progetto",
        body: "Ogni lavoratore registra l'entrata sul progetto giusto. Sai sempre chi è in quale cantiere in questo momento.",
        plan: "Project Basic",
      },
      {
        title: "Registrazione di lavoro e materiali extra",
        body: "Quando l'ambito si amplia in cantiere, i lavoratori registrano lavoro e materiali extra in tempo reale — con testo e foto — così nulla viene fatturato a memoria.",
        plan: "Project Basic",
      },
    ],
    compliance: {
      label: "Registrazione delle ore digitale",
      title: "Check-in con verifica della posizione per progetto per i team di installazione",
      body: "Done-it dà ai team di installazione il check-in con verifica della posizione per progetto, un conto alla rovescia delle ore residue in tempo reale e l'esportazione automatica per le buste paga — coprendo qualsiasi requisito locale di rilevazione delle ore. Per i team che lavorano nei cantieri edili belgi, si integra anche con CheckIn@Work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Il lavoratore apre il progetto sul telefono", body: "Indirizzo, contatto, ambito e file caricati sono proprio lì. Naviga con un tocco — niente chiamate all'ufficio per le indicazioni." },
      { step: "Le ore si registrano automaticamente sul lavoro giusto", body: "Il check-in collega ogni minuto lavorato al progetto corretto. Il budget residuo si aggiorna in tempo reale." },
      { step: "Il lavoro extra viene registrato in cantiere", body: "Modifiche di ambito, materiali extra e ritardi vengono registrati con testo e foto nel momento in cui accadono — pronti per la fattura." },
    ],
    stats: [
      { value: "In tempo reale", label: "vista delle ore residue per progetto" },
      { value: "0", label: "chiamate all'ufficio per gli indirizzi dei cantieri" },
      { value: "1 clic", label: "naviga verso qualsiasi indirizzo di progetto dall'app" },
    ],
    quote: {
      text: "Le ore finiscono sul progetto giusto automaticamente. Il calcolo dei costi di commessa è diventato improvvisamente facile — possiamo vedere il margine reale prima di chiudere un lavoro.",
      who: "Titolare, impresa di installazioni · 6 dipendenti",
      result: "Costo reale per lavoro, in tempo reale",
    },
    relatedSlugs: ["construction", "electricians", "hvac"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "field-service",
    name: "Assistenza sul campo",
    headline: "Rilevazione delle ore per i team di assistenza sul campo — chilometraggio, spese e ore dalla strada, tutto in un'unica app",
    intro: "Tecnici che visitano più clienti al giorno, ricevute nel cassetto portaoggetti, chilometraggio registrato a memoria. Done-it cattura ore, chilometri e spese sul campo — collegati al lavoro giusto nel momento in cui accadono.",
    pains: [
      {
        title: "Chilometraggio registrato giorni dopo — se mai lo è",
        body: "Chiedere ai tecnici di ricostruire i percorsi a fine settimana significa rimborsi imprecisi, contestazioni con i clienti e una pila crescente di chilometri non rivendicati.",
      },
      {
        title: "Spese su ricevute cartacee che spariscono",
        body: "Una ricevuta nel cassetto portaoggetti è un costo che potrebbe non finire mai in una fattura. Il costo reale per lavoro è sempre sottostimato.",
      },
      {
        title: "Nessuna vista in tempo reale di dove si trova la squadra",
        body: "Senza check-in in tempo reale, scopri dove si trova un tecnico solo quando qualcosa va storto e qualcuno chiama.",
      },
      {
        title: "Difficile vedere il margine reale per lavoro",
        body: "Quando manodopera, viaggi e spese vengono tracciati in sistemi separati — o non vengono tracciati affatto — non c'è mai un'unica cifra di costo per lavoro.",
      },
    ],
    features: [
      {
        title: "Cattura automatica del chilometraggio (Trax-it)",
        body: "Il GPS registra partenza, arrivo e distanza esatta per ogni spostamento. Configura i tipi di trasporto (auto propria, veicolo aziendale, car pooling) con le rispettive tariffe di rimborso. Nessun registro manuale.",
        plan: "Add-on",
      },
      {
        title: "Gestione delle spese sul campo",
        body: "I lavoratori fotografano una ricevuta sul campo; viene collegata al lavoro e pronta per il rimborso — non rincorsa a fine mese.",
        plan: "Time Basic",
      },
      {
        title: "Controllo dei costi collegato al progetto",
        body: "Ore, chilometraggio e spese confluiscono sul lavoro in tempo reale. Il costo per lavoro è visibile mentre il lavoro è ancora aperto.",
        plan: "Project Basic",
      },
      {
        title: "Check-in con verifica della posizione e panoramica della squadra in tempo reale",
        body: "Ogni tecnico registra l'entrata presso la sede del cliente. Vedi chi è dove, in questo momento — niente chiamate, niente supposizioni.",
      },
      {
        title: "Esportazione per le buste paga con un clic",
        body: "Ore e rimborsi chilometrici si esportano in modo pulito per il tuo fornitore di buste paga — con le tue tariffe chilometriche configurate applicate automaticamente.",
      },
    ],
    compliance: {
      label: "Rilevazione digitale di ore e chilometraggio",
      title: "Ore con GPS e chilometraggio automatico per i team di assistenza sul campo",
      body: "Done-it cattura il chilometraggio automaticamente per ogni spostamento, applica le tue tariffe di rimborso configurate e registra ogni chilometro sul cliente o progetto giusto. La sentenza Tyco dell'UE significa che il viaggio da casa al primo cliente conta come tempo di lavoro — Done-it traccia anche quello.",
      slug: "how-to-track-mileage-for-field-teams",
    },
    workflow: [
      { step: "Il tecnico parte per il primo intervento", body: "Il check-in avvia la registrazione dello spostamento. I chilometri vengono catturati automaticamente dalla partenza all'arrivo — nessun inserimento manuale." },
      { step: "Sul luogo del lavoro", body: "Le ore si collegano al progetto giusto. Lavoro extra, materiali e ricevute vengono registrati nell'app nel momento in cui accadono." },
      { step: "Fine mese senza il caos", body: "Ogni ora, chilometro e spesa è già collegato al lavoro corretto. Esporta verso le buste paga e fattura con cifre di costo reali." },
    ],
    stats: [
      { value: "Automatica", label: "cattura del chilometraggio — nessun registro manuale" },
      { value: "In tempo reale", label: "costo per lavoro: ore + viaggi + spese" },
      { value: "0", label: "chilometri persi — ogni spostamento catturato automaticamente" },
    ],
    quote: {
      text: "Il chilometraggio era un grattacapo mensile. Ora si registra da solo — ogni chilometro è collegato al cliente giusto prima ancora che torniamo in ufficio.",
      who: "Responsabile operativo, impresa di assistenza sul campo · 11 dipendenti",
      result: "Costo per lavoro accurato, ogni volta",
    },
    relatedSlugs: ["hvac", "maintenance", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "hvac",
    name: "HVAC",
    headline: "Rilevazione delle ore per i tecnici HVAC — calcolo dei costi di commessa, ricambi e viaggi in un'unica app",
    intro: "Ogni intervento ha tre costi: manodopera, ricambi e viaggi. Done-it cattura tutti e tre sul campo e li fa confluire sul lavoro giusto — così conosci il tuo margine prima di chiudere la fattura.",
    pains: [
      {
        title: "Gli interventi sono difficili da quotare con precisione",
        body: "Manodopera, ricambi e viaggi vivono in sistemi diversi — o in tasche diverse. Il costo reale di un intervento diventa chiaro solo molto tempo dopo che il tecnico ha lasciato il cantiere.",
      },
      {
        title: "Nessuna prova del tempo di servizio",
        body: "Quando un cliente contesta quanto è durata una riparazione, un foglio ore cartaceo è facile da contestare. Le marche temporali di entrata e uscita confermate dal GPS no.",
      },
      {
        title: "I contratti di manutenzione erodono il margine silenziosamente",
        body: "Sui contratti di manutenzione ricorrenti, i piccoli sforamenti si accumulano tra molte visite. Senza un contatore delle ore in tempo reale, scopri che il contratto è in perdita solo al rinnovo.",
      },
      {
        title: "I tecnici dimenticano di registrare le pause",
        body: "Le registrazioni manuali del tempo tralasciano le pause, portando a pagamenti eccessivi e a buste paga che non corrispondono alla realtà.",
      },
    ],
    features: [
      {
        title: "Rilevazione delle ore basata sul progetto",
        body: "Ogni intervento o contratto di manutenzione è un progetto. Ore, viaggi e materiali confluiscono in tempo reale — dandoti visibilità del margine in tempo reale.",
        plan: "Project Basic",
      },
      {
        title: "Lavoro e materiali extra, registrati in cantiere",
        body: "Quando un lavoro cresce, i tecnici registrano lavoro e ricambi extra con testo e foto prima di andare via. Nulla viene fatturato a memoria.",
        plan: "Project Basic",
      },
      {
        title: "Chilometraggio e costi di trasporto",
        body: "I chilometri per intervento vengono registrati automaticamente e imputati al lavoro. Configura le tariffe di rimborso per tipo di veicolo.",
        plan: "Add-on",
      },
      {
        title: "Detrazione automatica delle pause",
        body: "Le pause obbligatorie vengono sottratte automaticamente dal tempo totale — niente contestazioni, niente errori nelle buste paga.",
      },
      {
        title: "Check-in con verifica della posizione e prova di presenza",
        body: "Check-in con marca temporale e confermato dal GPS in ogni sede. Ogni registrazione ha la posizione verificata.",
      },
    ],
    compliance: {
      label: "Rilevazione digitale di ore e costi",
      title: "Calcolo completo dei costi di commessa per gli interventi HVAC",
      body: "Done-it dà ai team HVAC il check-in con verifica della posizione per intervento, la visibilità dei costi in tempo reale per lavoro (manodopera + viaggi + ricambi) e un'esportazione pulita per le buste paga — soddisfacendo qualsiasi requisito locale di rilevazione delle ore senza hardware aggiuntivo.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Il tecnico registra l'entrata in cantiere", body: "Il GPS marca temporalmente l'arrivo e collega le ore all'intervento o al contratto di manutenzione. Niente carta, niente terminale." },
      { step: "Il lavoro extra viene registrato sul posto", body: "Ricambi usati, ore extra e modifiche di ambito vengono registrati nell'app con foto — prima di lasciare il cantiere." },
      { step: "Il costo del lavoro è visibile immediatamente", body: "Manodopera, viaggi e materiali si sommano sul progetto in tempo reale. Vedi il margine prima di emettere la fattura." },
    ],
    stats: [
      { value: "3", label: "tipi di costo catturati per intervento: manodopera, viaggi, ricambi" },
      { value: "In tempo reale", label: "visibilità del margine per contratto di servizio" },
      { value: "0", label: "sorprese al momento della fattura — costi visibili in tempo reale" },
    ],
    quote: {
      text: "Finalmente sappiamo quanto ci costa davvero ogni contratto di servizio. Le sorprese sul margine sono finite il giorno in cui abbiamo iniziato a usare Done-it.",
      who: "Titolare, impresa HVAC · 7 tecnici",
      result: "Costo reale per intervento, in tempo reale",
    },
    relatedSlugs: ["installation", "electricians", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "time-tracking-for-field-teams-complete-guide",
    ],
  },

  {
    slug: "electricians",
    name: "Elettricisti",
    headline: "Rilevazione delle ore per gli elettricisti — ore per progetto, controllo del budget e navigazione in un'unica app",
    intro: "Ore per progetto, un conto alla rovescia in tempo reale del budget residuo e navigazione verso il cantiere con un solo pulsante — pensato per i piccoli team di elettricisti che hanno bisogno del controllo dei progetti senza il sovraccarico amministrativo.",
    pains: [
      {
        title: "Le ore non sono collegate al progetto giusto",
        body: "Quando gli elettricisti lavorano su più lavori in una settimana, allocare manualmente le ore a fine giornata porta a costi attribuiti erroneamente, contestazioni di fatturazione e buste paga che non sai spiegare.",
      },
      {
        title: "Gli sforamenti di budget individuati troppo tardi",
        body: "Senza una vista in tempo reale delle ore consumate rispetto a quelle previste, scopri che un progetto è andato fuori budget quando emetti la fattura — non quando potresti ancora reagire.",
      },
      {
        title: "Tempo perso a trovare il cantiere",
        body: "Indirizzi digitati in un'app separata, chiamate all'ufficio per le indicazioni, arrivi in ritardo presso i clienti. Ogni minuto perso è un minuto fatturabile perso.",
      },
      {
        title: "Difficile riconciliare le ore dei subappaltatori",
        body: "Sui lavori più grandi, gli elettricisti subappaltatori tracciano le proprie ore separatamente. Ottenere una vista combinata dei costi della manodopera a fine progetto è un lavoro manuale e soggetto a errori.",
      },
    ],
    features: [
      {
        title: "Ore di progetto con budget residuo in tempo reale",
        body: "Ogni minuto lavorato è collegato automaticamente al progetto giusto. Un conto alla rovescia in tempo reale mostra quante ore restano rispetto al budget — per te e per il lavoratore in cantiere.",
        plan: "Project Basic",
      },
      {
        title: "Navigazione verso il cantiere con un solo pulsante",
        body: "L'indirizzo del progetto è nell'app. Un tocco apre la navigazione — niente ricerche, niente app di mappe separata, niente chiamate all'ufficio.",
      },
      {
        title: "Check-in del lavoratore per progetto",
        body: "Ogni elettricista registra l'entrata sul lavoro giusto. Sai sempre chi è in quale cantiere in questo momento.",
        plan: "Project Basic",
      },
      {
        title: "Lavoro extra registrato in cantiere",
        body: "Quando l'ambito cambia — un circuito imprevisto, materiali aggiuntivi — viene registrato nell'app con foto, prima che la squadra vada via.",
        plan: "Project Basic",
      },
      {
        title: "Rilevazione degli straordinari con compensazione flessibile",
        body: "Gli straordinari vengono registrati nel momento in cui accadono. I lavoratori scelgono di farseli pagare o di convertirli in tempo libero.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Check-in con verifica della posizione per progetto",
      title: "Controllo del budget in tempo reale e check-in con verifica della posizione per i team di elettricisti",
      body: "Done-it dà agli elettricisti il check-in con verifica della posizione per progetto, un budget di ore residue in tempo reale e l'esportazione per le buste paga — soddisfacendo qualsiasi requisito locale di rilevazione delle ore. Per i team nei cantieri edili belgi, si integra anche con CheckIn@Work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "L'elettricista naviga verso il lavoro", body: "Indirizzo e ambito sono già nell'app. Un tocco apre la navigazione — niente chiamate, niente confusione." },
      { step: "Le ore si collegano automaticamente al progetto giusto", body: "Il check-in in cantiere collega ogni minuto lavorato al progetto corretto. Il contatore delle ore residue si aggiorna in tempo reale." },
      { step: "Le modifiche di ambito restano agli atti", body: "Lavoro e materiali extra vengono registrati in cantiere, prima di andare via. La fattura riflette ciò che è realmente accaduto." },
    ],
    stats: [
      { value: "In tempo reale", label: "vista delle ore residue per progetto" },
      { value: "0", label: "chiamate all'ufficio per gli indirizzi dei cantieri" },
      { value: "1 tocco", label: "per registrare l'entrata in qualsiasi cantiere — nessun terminale necessario" },
    ],
    quote: {
      text: "Sappiamo in tempo reale se un progetto sta andando fuori budget. Solo questo ci ha salvato da due brutte sorprese quest'anno.",
      who: "Titolare, impresa elettrica · 5 elettricisti",
      result: "Sforamenti di budget colti prima che accadano",
    },
    relatedSlugs: ["installation", "hvac", "plumbing"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "plumbing",
    name: "Idraulica",
    headline: "Rilevazione delle ore per i team di idraulica — check-in rapido, buste paga accurate e prova di presenza per ogni intervento",
    intro: "Interventi d'emergenza, più lavori al giorno, ore compilate a memoria a fine settimana — o per niente. Done-it lo rende un lavoro da un tocco in ogni sede e una busta paga da un clic a fine mese.",
    pains: [
      {
        title: "Ore di intervento ricostruite a memoria",
        body: "Con diversi lavori in un giorno, gli idraulici raramente hanno tempo di compilare fogli ore accurati. Le ore vengono arrotondate o dimenticate, e le buste paga sono al massimo una stima.",
      },
      {
        title: "Nessuna prova del tempo in cantiere",
        body: "Quando un cliente contesta quanto è durata una riparazione, non c'è un record oggettivo. Un check-in digitale confermato dal GPS è facile da produrre — un foglio ore cartaceo no.",
      },
      {
        title: "Chilometraggio tra i lavori non tracciato",
        body: "Gli spostamenti tra gli interventi si accumulano nell'arco della settimana e del mese, ma senza la cattura automatica raramente vengono fatturati o rimborsati correttamente.",
      },
      {
        title: "La preparazione delle buste paga è lenta",
        body: "A fine mese, rincorrere i fogli ore, correggere gli errori e costruire l'esportazione per le buste paga richiede un tempo che il tuo team amministrativo non dovrebbe impiegare.",
      },
    ],
    features: [
      {
        title: "Check-in con verifica della posizione e un tocco a ogni intervento",
        body: "Arrivo e partenza registrati con un tocco, con verifica della posizione. Abbastanza rapido per le chiamate d'emergenza; abbastanza preciso per le contestazioni.",
        plan: "Time Basic",
      },
      {
        title: "Detrazione automatica delle pause",
        body: "Le pause obbligatorie vengono sottratte automaticamente dal tempo totale — niente da compilare, niente errori nelle buste paga.",
        plan: "Time Basic",
      },
      {
        title: "Chilometraggio catturato per spostamento",
        body: "Configura i tipi di trasporto e le tariffe di rimborso. I chilometri si registrano automaticamente e si collegano al cliente o progetto corretto.",
        plan: "Add-on",
      },
      {
        title: "Gestione delle assenze e dei certificati medici",
        body: "I lavoratori inviano i certificati medici in digitale dall'app. Crea i tipi di assenza che la tua squadra usa davvero, inclusi i ritardi per maltempo.",
      },
      {
        title: "Esportazione per le buste paga con un clic",
        body: "Tutti i dati di ore e chilometri si esportano in modo pulito per il tuo fornitore di buste paga — nessuna preparazione manuale, niente Excel.",
        plan: "Time Basic",
      },
    ],
    compliance: {
      label: "Registrazione delle ore digitale",
      title: "Check-in con verifica della posizione rapido per ogni intervento",
      body: "Done-it copre qualsiasi requisito locale di rilevazione delle ore per i team di idraulica — check-in con verifica della posizione e un tocco per intervento, detrazione automatica delle pause e un'esportazione pulita per le buste paga. Niente fogli ore cartacei, nessuna ricostruzione manuale.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "L'idraulico arriva all'intervento", body: "Un tocco registra l'entrata. Il GPS registra la posizione e l'ora. Abbastanza rapido per un lavoro d'emergenza." },
      { step: "La partenza si tocca uscendo", body: "Il check-out chiude la registrazione del tempo. Il chilometraggio verso il lavoro successivo inizia a registrarsi automaticamente." },
      { step: "La fine mese è un clic", body: "Tutti gli interventi, le ore e i chilometri si esportano in modo pulito. La preparazione delle buste paga passa da un giorno a pochi minuti." },
    ],
    stats: [
      { value: "1 tocco", label: "per registrare l'entrata — abbastanza rapido per le chiamate d'emergenza" },
      { value: "1 clic", label: "esportazione per le buste paga verso il tuo fornitore" },
      { value: "0", label: "ore ricostruite a memoria a fine mese" },
    ],
    quote: {
      text: "Prima passavamo una mattinata a fine mese a ricostruire le ore. Ora esportiamo e basta. Il fornitore di buste paga le riceve pulite.",
      who: "Titolare, impresa di idraulica · 4 idraulici",
      result: "Preparazione delle buste paga passata da ore a minuti",
    },
    relatedSlugs: ["hvac", "electricians", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "landscaping",
    name: "Giardinaggio",
    headline: "Rilevazione delle ore per le imprese di giardinaggio — squadre stagionali, sedi multiple e chilometraggio in un'unica panoramica",
    intro: "L'alta stagione significa molte sedi, squadre che cambiano e tanti chilometri. Done-it mantiene organizzata ogni ora, ogni assenza e ogni spostamento — così puoi concentrarti sul lavoro, non sulle scartoffie.",
    pains: [
      {
        title: "Le squadre stagionali sono complesse da gestire",
        body: "Lavoratori temporanei, orari mutevoli e dimensioni variabili dei team rendono quasi impossibile una rilevazione accurata delle ore con la carta. Quando riconcili a fine mese, manca sempre qualcosa.",
      },
      {
        title: "Ore distribuite su molte sedi",
        body: "Una squadra che visita tre o quattro sedi al giorno genera ore che vanno suddivise correttamente. L'inserimento manuale porta a errate attribuzioni e a buste paga contestate.",
      },
      {
        title: "Le interruzioni per maltempo sono difficili da documentare",
        body: "Il fermo per maltempo è un evento reale nel giardinaggio. Senza un record digitale, documentare le interruzioni e adeguare le retribuzioni è lento e soggetto a errori.",
      },
      {
        title: "Il chilometraggio tra le sedi resta non tracciato",
        body: "Furgoni aziendali, veicoli personali e car pooling si incrociano tra le sedi tutto il giorno. Senza la cattura automatica, quei chilometri non entrano mai nel quadro dei costi.",
      },
    ],
    features: [
      {
        title: "Panoramica delle squadre multi-sede",
        body: "Vedi tutte le squadre, tutte le sedi in tempo reale. Sai chi lavora dove, in questo momento — non a fine mese.",
      },
      {
        title: "Programmazione dei turni per i team stagionali",
        body: "Pianifica chi lavora dove, gestisci le modifiche dell'ultimo minuto e invia il programma ai telefoni della squadra — senza una telefonata a ciascuna persona.",
        plan: "Time Pro",
      },
      {
        title: "Tipi di assenza personalizzati",
        body: "Crea tipi di assenza personalizzati per il tuo settore: interruzioni stagionali per maltempo, congedo parentale, giorni di malattia. I lavoratori caricano la documentazione in digitale dall'app.",
      },
      {
        title: "Chilometraggio con tracciamento del car pooling",
        body: "Configura i tipi di trasporto (furgone aziendale, auto personale, conducente vs. passeggero in car pooling) con le rispettive tariffe. I chilometri si registrano automaticamente per spostamento.",
        plan: "Add-on",
      },
      {
        title: "Rilevazione degli straordinari con conversione in ferie",
        body: "Gli straordinari dell'alta stagione si accumulano in fretta. Ogni lavoratore può scegliere di farseli pagare o di accantonare le ore come tempo libero — tracciate in modo trasparente.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Registrazione delle ore digitale",
      title: "Check-in con verifica della posizione e tracciamento delle assenze per i team stagionali di giardinaggio",
      body: "Done-it dà ai team di giardinaggio la registrazione delle ore digitale in tutte le sedi e per il personale stagionale — check-in con verifica della posizione, tipi di assenza personalizzati per le interruzioni per maltempo e un'esportazione pulita per le buste paga che soddisfa qualsiasi requisito locale.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "La squadra registra l'entrata alla prima sede", body: "Il GPS registra l'arrivo. Le ore si suddividono automaticamente sulla sede e sul progetto giusti — nessuna allocazione manuale a fine giornata." },
      { step: "Il maltempo ferma il lavoro", body: "Il caposquadra registra l'interruzione dall'app — un record digitale pulito, niente scartoffie aggiuntive." },
      { step: "Fine mese: tutte le sedi, tutte le squadre, un'unica esportazione", body: "Ore, assenze e chilometraggio di ogni sede e di ogni membro della squadra si esportano in modo pulito per le buste paga." },
    ],
    stats: [
      { value: "Automatica", label: "cattura del chilometraggio — niente più registro" },
      { value: "In tempo reale", label: "panoramica delle squadre in tutte le sedi" },
      { value: "1 clic", label: "esportazione per le buste paga per tutte le sedi e tutte le squadre" },
    ],
    quote: {
      text: "L'alta stagione significava caos nelle buste paga. Ora il nostro caposquadra controlla l'app, tutti sono conteggiati, e io esporto le ore l'ultimo giorno del mese.",
      who: "Titolare, impresa di giardinaggio · 12 lavoratori stagionali",
      result: "Niente più ricostruzione delle buste paga a fine mese",
    },
    relatedSlugs: ["maintenance", "construction", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "maintenance",
    name: "Manutenzione",
    headline: "Rilevazione delle ore per i team di manutenzione — visite ricorrenti, costo per contratto e prova di presenza in un'unica app",
    intro: "I contratti di manutenzione ricorrenti vivono o muoiono sulla visibilità dei costi. Done-it registra ogni visita, traccia le ore rispetto al budget del contratto e dà a te — e ai tuoi clienti — la prova di presenza per ogni intervento.",
    pains: [
      {
        title: "Nessuna visibilità dei costi per contratto di manutenzione",
        body: "Quando le ore delle visite ricorrenti non vengono tracciate rispetto al contratto specifico, non sai se un cliente è redditizio fino alla revisione di fine anno — quando ormai è troppo tardi per riquotare.",
      },
      {
        title: "I clienti si aspettano la prova di ciò che è stato fatto",
        body: "I facility manager e i proprietari di immobili vogliono sempre più registrazioni delle presenze: chi è venuto, quando è arrivato, cosa ha fatto. Un registro cartaceo presentato settimane dopo non soddisfa i requisiti degli SLA.",
      },
      {
        title: "Visite ricorrenti difficili da programmare e registrare",
        body: "Turni a rotazione su molte sedi ricorrenti — con personale che si sostituisce a vicenda — creano un rompicapo di programmazione e rilevazione delle ore che i processi manuali non possono risolvere in modo affidabile.",
      },
      {
        title: "Le interruzioni per maltempo riguardano anche la manutenzione all'aperto",
        body: "Cura del verde, manutenzione delle facciate e lavori sugli impianti all'aperto affrontano tutti interruzioni legate al maltempo. Documentarle correttamente richiede una traccia digitale.",
      },
    ],
    features: [
      {
        title: "Tracciamento dei costi basato sul contratto",
        body: "Ogni contratto di manutenzione è un progetto. Le ore si registrano automaticamente su di esso, dandoti una cifra di costo in tempo reale e un conto alla rovescia delle ore residue per ogni cliente.",
        plan: "Project Basic",
      },
      {
        title: "Prova di presenza confermata dal GPS",
        body: "I check-in con marca temporale e posizione verificata sono un record di chi era in cantiere, quando e per quanto tempo — pronti da condividere con i clienti su richiesta.",
      },
      {
        title: "Aggiornamenti con foto dal cantiere",
        body: "I lavoratori segnalano problemi, attività completate o lavoro extra direttamente dall'app — con testo e foto. I clienti ottengono un record in tempo reale; tu hai la documentazione per la fatturazione.",
        plan: "Project Basic",
      },
      {
        title: "Programmazione dei turni ricorrenti",
        body: "Pianifica le visite ricorrenti su tutti i contratti, assegna i lavoratori e gestisci le sostituzioni — senza una telefonata a ciascuna persona.",
        plan: "Time Pro",
      },
      {
        title: "Tipi di assenza per la manutenzione all'aperto",
        body: "Configura le interruzioni per maltempo e altri tipi di assenza. I lavoratori le registrano dall'app; la documentazione si carica in digitale.",
      },
    ],
    compliance: {
      label: "Rilevazione delle ore basata sul contratto",
      title: "Visibilità dei costi in tempo reale per contratto di manutenzione",
      body: "Done-it dà ai team di manutenzione la prova di presenza confermata dal GPS per contratto, tipi di assenza personalizzati e una vista dei costi in tempo reale per ogni cliente ricorrente — soddisfacendo qualsiasi requisito locale di rilevazione delle ore senza hardware aggiuntivo.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Il lavoratore arriva alla sede di manutenzione", body: "Il check-in con verifica della posizione registra l'ora di arrivo e collega le ore al contratto corretto. La prova di presenza viene creata automaticamente." },
      { step: "Attività completate, problemi registrati", body: "Il lavoro completato ed eventuali problemi vengono segnalati dall'app con foto. I clienti possono vedere esattamente cosa è stato fatto." },
      { step: "Costo per contratto visibile immediatamente", body: "Le ore si accumulano rispetto al budget del contratto in tempo reale. Conosci il margine prima che il contratto si rinnovi." },
    ],
    stats: [
      { value: "In tempo reale", label: "costo per contratto di manutenzione" },
      { value: "GPS", label: "prova di presenza pronta per i clienti su richiesta" },
      { value: "0", label: "rinnovi di contratto senza conoscere il tuo margine" },
    ],
    quote: {
      text: "Possiamo dire a un facility manager esattamente chi era in cantiere e quando, in due minuti. Quel livello di prova ha cambiato il modo in cui i clienti ci vedono.",
      who: "Responsabile operativo, impresa di manutenzione · 8 lavoratori",
      result: "Contestazioni dei clienti ridotte a zero",
    },
    relatedSlugs: ["cleaning", "landscaping", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
  {
    slug: "retail",
    name: "Negozi e retail",
    headline: "Rilevazione presenze per negozi — timbratura dei turni, sostituzioni ed export pronto per le paghe, in un'unica app",
    intro: "Turni di apertura, turni serali e qualcuno che copre sempre qualcun altro. Done-it dà a ogni negozio un registro digitale pulito di chi ha lavorato in quale turno — timbrato dal proprio telefono, pronto per le paghe a fine mese.",
    pains: [
      {
        title: "I cambi turno non arrivano mai alle paghe",
        body: "Qualcuno copre un collega malato, qualcuno resta un'ora in più alla chiusura. Finisce su un post-it, o da nessuna parte — e le paghe sono già sbagliate prima di iniziare.",
      },
      {
        title: "Più negozi, un unico ciclo paghe",
        body: "Il personale si sposta tra le filiali, i part-time lavorano su due sedi. Raccogliere le ore per negozio e unirle a mano costa una giornata al mese.",
      },
      {
        title: "Pause e straordinari a occhio",
        body: "Le pause di legge vengono scalate a memoria e le ore extra si sistemano in via informale — le prime due cose che un'ispezione va a controllare.",
      },
    ],
    features: [
      {
        title: "Timbrare in negozio",
        body: "Un tap dal proprio telefono all'inizio del turno. Nessun terminale alla cassa, nessuna tessera condivisa, nessun foglio appeso in magazzino.",
      },
      {
        title: "Ore per negozio, personale tra le filiali",
        body: "Ogni negozio è un progetto. Chi copre un'altra filiale timbra su quella filiale — vedi le ore dove sono state davvero lavorate.",
      },
      {
        title: "Pause scalate in automatico",
        body: "Imposti la regola una volta e vale per ogni turno. Gli straordinari vengono registrati nel momento in cui maturano e ogni dipendente sceglie tra pagamento e recupero.",
      },
    ],
    compliance: {
      label: "Rilevazione presenze a turni",
      title: "Un registro difendibile di ogni turno lavorato",
      body: "Done-it conserva un registro digitale e oggettivo di ogni entrata e uscita, con pause e straordinari — il registro che un audit o una contestazione sulle paghe richiede.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Il personale timbra il turno", body: "Un tap dal telefono all'inizio del turno. Le sostituzioni in un'altra filiale vengono registrate su quella filiale." },
      { step: "Vedi chi è in servizio, per negozio", body: "Una vista in tempo reale su tutte le sedi — individui un turno di apertura scoperto prima che il negozio apra." },
      { step: "Paghe in un unico export", body: "Ore, pause e straordinari per dipendente e per negozio, esportati pronti per il vostro consulente paghe." },
    ],
    stats: [
      { value: "1 tap", label: "per timbrare — nessun terminale alla cassa" },
      { value: "Live", label: "chi è in servizio, in ogni negozio" },
      { value: "0", label: "cambi turno persi tra il punto vendita e le paghe" },
    ],
    quote: {
      text: "Due negozi e tanti part-time. Prima metà delle sostituzioni si sistemava a memoria. Ora ogni ora è sul negozio giusto e le paghe si chiudono in pochi minuti.",
      who: "Titolare, retail · 11 dipendenti",
      result: "Preparazione paghe da un giorno a meno di un'ora",
    },
    relatedSlugs: ["hospitality", "beauty", "offices"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "offices",
    name: "Uffici e servizi professionali",
    headline: "Rilevazione presenze per uffici — ore per cliente e per progetto, senza fogli di calcolo",
    intro: "Commercialisti, notai, architetti, agenzie e società di consulenza fatturano il proprio tempo — o quantomeno devono sapere dov'è finito. Done-it registra le ore sul cliente e sul progetto giusti, dalla scrivania o fuori sede.",
    pains: [
      {
        title: "Ore fatturabili ricostruite il venerdì",
        body: "Il tempo annotato a fine settimana è tempo stimato. Ore intere spariscono in silenzio tra il lavoro e la fattura.",
      },
      {
        title: "Nessuna idea di quanto costi davvero un cliente",
        body: "Il compenso è stato concordato mesi fa. Se il lavoro ci sta ancora dentro si scopre solo quando qualcuno fa i conti — di solito troppo tardi.",
      },
      {
        title: "Il lavoro ibrido rende il registro incompleto",
        body: "Una parte del team è in ufficio, una a casa, una dal cliente. La presenza smette di essere un dato e diventa una ricostruzione.",
      },
    ],
    features: [
      {
        title: "Ore su cliente e progetto",
        body: "Ogni registrazione finisce su un progetto, così vedi le ore per cliente senza esportare nulla e senza chiedere a nessuno.",
      },
      {
        title: "Funziona alla scrivania e fuori sede",
        body: "La stessa app su telefono o browser. Una visita da un cliente conta come lavoro su quel cliente, trasferta inclusa se lo desideri.",
      },
      {
        title: "Straordinari e ferie in un solo posto",
        body: "Ore extra registrate nel momento in cui maturano, ferie richieste e approvate nell'app — niente foglio parallelo con chi è assente e quando.",
      },
    ],
    compliance: {
      label: "Rilevazione presenze per cliente",
      title: "Un registro oggettivo delle ore lavorate",
      body: "Done-it conserva un registro digitale e a prova di manomissione dell'orario di lavoro per dipendente — la base sia per le paghe sia per una fattura al cliente difendibile.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Apri un progetto cliente", body: "Lo configuri una volta con il cliente e, se fatturi a budget, con le ore che avevi previsto." },
      { step: "Il team ci registra il tempo", body: "Dalla scrivania o dal telefono. Ogni ora finisce su un cliente, non nella memoria di qualcuno." },
      { step: "Vedi le ore per cliente, esporti per le paghe", body: "Totali in tempo reale per progetto e un export pulito per paghe o fatturazione a fine mese." },
    ],
    stats: [
      { value: "Per cliente", label: "ore visibili mentre il lavoro è ancora in corso" },
      { value: "In sede o fuori", label: "stesso registro, telefono e browser" },
      { value: "1 export", label: "per paghe e fatturazione" },
    ],
    quote: {
      text: "Ogni venerdì ricostruivamo la settimana. Ora le ore sono già sul cliente giusto e vedo quali pratiche stanno erodendo il compenso.",
      who: "Socio, studio commercialistico · 8 dipendenti",
      result: "Ore fatturabili recuperate ogni mese",
    },
    relatedSlugs: ["retail", "healthcare", "wellness"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "beauty",
    name: "Saloni e cura della persona",
    headline: "Rilevazione presenze per saloni — turni, ore part-time e paghe senza agenda cartacea",
    intro: "Parrucchieri, barbieri, centri estetici e nail studio vivono di turni e contratti part-time. Done-it registra chi ha lavorato e quando, scala le pause in automatico e consegna alle paghe un file pulito.",
    pains: [
      {
        title: "Contratti part-time, variazioni infinite",
        body: "Tre giorni una settimana, quattro quella dopo, un sabato scambiato con un martedì. Ore contrattuali e ore lavorate divergono in fretta.",
      },
      {
        title: "L'agenda non è un registro presenze",
        body: "Gli appuntamenti dicono cosa è stato prenotato, non chi ha lavorato. Quando lo chiedono le paghe o un'ispezione, il gestionale delle prenotazioni è la risposta sbagliata.",
      },
      {
        title: "Ore extra sistemate a voce",
        body: "Restare oltre l'orario per un ultimo cliente si ricorda, si promette e a volte si dimentica. È il tipo di buco che diventa una contestazione.",
      },
    ],
    features: [
      {
        title: "Timbrare in salone",
        body: "Un tap dal proprio telefono all'inizio del turno — nessun terminale condiviso alla reception, nessun foglio firme accanto allo specchio.",
      },
      {
        title: "Pause e regole part-time una volta sola",
        body: "Imposti la regola delle pause e l'orario standard una volta; valgono per ogni turno, con eccezioni per persona dove il contratto è diverso.",
      },
      {
        title: "Straordinari, pagati o recuperati",
        body: "Le ore extra vengono registrate mentre si lavorano e ogni dipendente sceglie tra pagamento e recupero — si chiude nell'app, non a memoria.",
      },
    ],
    compliance: {
      label: "Rilevazione presenze digitale",
      title: "Un registro che regge oltre l'agenda appuntamenti",
      body: "Done-it registra l'orario di lavoro effettivo per dipendente, con pause e straordinari — separato dalle prenotazioni e difendibile quando viene verificato.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Il personale timbra il turno", body: "Un tap all'arrivo. Le pause vengono scalate con la regola impostata una volta." },
      { step: "Vedi la giornata mentre scorre", body: "Chi è in servizio, chi è in pausa, chi è rimasto oltre l'orario — in tempo reale, senza chiedere." },
      { step: "Le paghe ricevono un file pulito", body: "Ore lavorate, pause e straordinari per dipendente, esportati per il vostro consulente paghe." },
    ],
    stats: [
      { value: "1 tap", label: "per timbrare — niente di condiviso, niente su carta" },
      { value: "Auto", label: "pause scalate su ogni turno" },
      { value: "0", label: "ore extra sistemate a memoria" },
    ],
    quote: {
      text: "Sono tutti part-time e ogni settimana è diversa. L'app lo registra e basta — ho smesso di ricostruire il mese in un foglio di calcolo.",
      who: "Titolare, salone di parrucchiere · 6 dipendenti",
      result: "Niente più ore contestate a fine mese",
    },
    relatedSlugs: ["wellness", "retail", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "wellness",
    name: "Salute e benessere",
    headline: "Rilevazione presenze per palestre, studi e spa — turni, corsi e paghe in un'unica app",
    intro: "Palestre, studi fitness, centri di fisioterapia e spa hanno orari di apertura lunghi e personale che ruota. Done-it registra chi ha coperto quale turno, scala le pause in automatico ed esporta le paghe senza intoppi.",
    pains: [
      {
        title: "Orari lunghi, coperture a rotazione",
        body: "Turni mattutini, corsi serali e weekend, coperti da un mix di full-time, part-time e collaboratori. Il planning è un piano, non un registro.",
      },
      {
        title: "Ore di corso e ore in sala si confondono",
        body: "Un istruttore tiene due corsi e nel mezzo copre la reception. Pagarlo correttamente significa sapere cosa è stato davvero lavorato, non cosa era in programma.",
      },
      {
        title: "Più sedi, un'unica amministrazione",
        body: "Il personale si sposta tra gli studi. Unire le loro ore a mano ogni mese è lento e pieno di errori.",
      },
    ],
    features: [
      {
        title: "Timbratura per studio",
        body: "Un tap dal telefono nella sede in cui si lavora. La copertura in un altro studio finisce sulle ore di quello studio.",
      },
      {
        title: "Vista live di chi è in servizio",
        body: "Vedi la sala in ogni sede in tempo reale — un turno mattutino scoperto si vede prima dell'apertura, non dopo.",
      },
      {
        title: "Pause e straordinari gestiti",
        body: "Regole delle pause applicate in automatico, ore extra registrate mentre maturano con pagamento o recupero a scelta del dipendente.",
      },
    ],
    compliance: {
      label: "Rilevazione turni multi-sede",
      title: "Un registro oggettivo per ogni turno e ogni sede",
      body: "Done-it conserva un registro digitale dell'orario di lavoro per dipendente e per sede — la base per una retribuzione corretta e per qualsiasi verifica sulle ore lavorate.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Il personale timbra in studio", body: "Un tap all'inizio del turno, nella sede in cui si lavora." },
      { step: "Le coperture si vedono in tempo reale", body: "Chi è in servizio e in quale sede in questo momento, così i buchi si coprono prima che gli iscritti se ne accorgano." },
      { step: "Un export per le paghe", body: "Ore, pause e straordinari per dipendente e per studio, pronti per il consulente paghe." },
    ],
    stats: [
      { value: "Per studio", label: "ore, anche quando il personale copre altrove" },
      { value: "Live", label: "la sala in tempo reale, in tutte le sedi" },
      { value: "Auto", label: "pause e straordinari, nessun calcolo manuale" },
    ],
    quote: {
      text: "Due studi, orari lunghi e tante sostituzioni. Ora le ore finiscono sulla sede giusta e le paghe sono un unico file.",
      who: "Responsabile, studio fitness · 14 dipendenti",
      result: "Planning e paghe finalmente coincidono",
    },
    relatedSlugs: ["beauty", "healthcare", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "hospitality",
    name: "Ristorazione e ospitalità",
    headline: "Rilevazione presenze per bar, ristoranti e hotel — turni, pause e straordinari, chiusi in modo equo",
    intro: "Turni spezzati, chiusure tardive e un sabato pieno che sfora. Done-it registra le ore come vengono davvero lavorate, scala le pause secondo la vostra regola e consegna alle paghe un unico file pulito.",
    pains: [
      {
        title: "Il turno non finisce mai quando dice il planning",
        body: "Il servizio va per le lunghe, la chiusura porta via un'altra mezz'ora. Quei minuti sono lavoro vero, e sistemarli a memoria è così che nascono le contestazioni.",
      },
      {
        title: "I turni spezzati sono difficili da registrare",
        body: "Servizio a pranzo, una pausa, poi la cena. Sulla carta diventa un unico blocco lungo o due stime — nessuna delle due è la verità.",
      },
      {
        title: "Turnover alto, inserimenti continui",
        body: "Personale nuovo ogni stagione. Un sistema che richiede formazione non sopravvive alla prima settimana piena.",
      },
    ],
    features: [
      {
        title: "Entrata e uscita per ogni servizio",
        body: "Un tap all'inizio e alla fine di ogni blocco, così i turni spezzati restano registrati come sono andati — non fusi in uno solo.",
      },
      {
        title: "Pause secondo la vostra regola",
        body: "Pause obbligatorie scalate in automatico, con eccezioni per persona dove serve al contratto. Nessun calcolo manuale a fine mese.",
      },
      {
        title: "Straordinari chiusi nell'app",
        body: "La mezz'ora in più dopo la chiusura viene registrata nel momento in cui si lavora e viene pagata o recuperata come permesso — sceglie il dipendente.",
      },
    ],
    compliance: {
      label: "Rilevazione turni spezzati",
      title: "Un registro difendibile delle ore in un team ad alto turnover",
      body: "Done-it conserva un registro digitale e oggettivo di ogni turno, pausa e ora extra — esattamente ciò che una verifica sulle paghe o un'ispezione del lavoro chiede di vedere.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Il personale timbra per il servizio", body: "Un tap all'inizio del blocco e lo stesso alla fine. I turni spezzati restano spezzati." },
      { step: "Vedi la sala in tempo reale", body: "Chi è in servizio stasera, chi è ancora in pausa, chi è rimasto oltre la chiusura." },
      { step: "Le paghe ricevono le ore reali", body: "Tempo lavorato, pause e straordinari per dipendente, esportati in un unico file." },
    ],
    stats: [
      { value: "Turni spezzati", label: "registrati come due blocchi, non come una stima" },
      { value: "Auto", label: "pause scalate secondo la vostra regola" },
      { value: "1 min", label: "per inserire un nuovo assunto — usa il suo telefono" },
    ],
    quote: {
      text: "La chiusura sfora sempre. Ora quei minuti vengono registrati invece che discussi, e il personale può recuperarli come permesso.",
      who: "Titolare, ristorante · 17 dipendenti",
      result: "Meno contestazioni, paghe più veloci",
    },
    relatedSlugs: ["retail", "wellness", "beauty"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "healthcare",
    name: "Sanità e assistenza",
    headline: "Rilevazione presenze per team di assistenza — turni, visite domiciliari e qualifiche, in un unico registro",
    intro: "Ambulatori, team di assistenza domiciliare e case di riposo ruotano intorno a turni, visite e obblighi documentali stringenti. Done-it registra il tempo lavorato per turno e per visita, e tiene i documenti dei dipendenti dove sai ritrovarli.",
    pains: [
      {
        title: "Visite e turni in sistemi separati",
        body: "Le visite domiciliari stanno in uno strumento, le ore pianificate in un altro e alle paghe arriva una terza versione. Riconciliarli è un lavoro mensile.",
      },
      {
        title: "Ore notturne e festive valorizzate male",
        body: "Le ore disagiate hanno tariffe diverse. Quando il registro è approssimativo, lo è anche la retribuzione — e la correzione arriva dopo.",
      },
      {
        title: "I certificati scadono in silenzio",
        body: "Qualifiche e certificazioni contano nell'assistenza, e restano in una cartella che nessuno apre finché non arriva un'ispezione.",
      },
    ],
    features: [
      {
        title: "Timbratura per turno o per visita",
        body: "Un tap all'inizio del turno, oppure a ogni indirizzo del cliente per l'assistenza domiciliare — con verifica della posizione dove serve la prova.",
      },
      {
        title: "Ore suddivise come le paghi",
        body: "Ore notturne, festive e ordinarie vengono registrate distintamente, così l'export per le paghe rispecchia già come il lavoro viene effettivamente retribuito.",
      },
      {
        title: "Documenti dei dipendenti in un unico posto",
        body: "Contratti, certificati e documenti d'identità archiviati accanto alla persona a cui appartengono — si trovano in pochi secondi quando qualcuno li chiede.",
      },
    ],
    compliance: {
      label: "Registrazione turni e visite",
      title: "Registri che reggono un'ispezione",
      body: "Done-it conserva un registro digitale e oggettivo dell'orario di lavoro per dipendente — ore, pause e suddivisione delle ore disagiate — insieme ai documenti di ogni persona.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Timbri per il turno o la visita", body: "Un tap in sede o all'indirizzo del cliente, con verifica della posizione dove serve la prova." },
      { step: "Le ore finiscono nella voce giusta", body: "Ore ordinarie, notturne e festive registrate distintamente mentre maturano." },
      { step: "Export per le paghe", body: "Un file pulito per periodo, già suddiviso come lo paghi." },
    ],
    stats: [
      { value: "Per visita", label: "o per turno — a seconda di come lavora il team" },
      { value: "Distinte", label: "ore notturne e festive, registrate come lavorate" },
      { value: "Archiviati", label: "certificati conservati per dipendente" },
    ],
    quote: {
      text: "Visite domiciliari e turni pianificati stanno finalmente nello stesso registro. Le paghe hanno smesso di essere un lavoro di riconciliazione.",
      who: "Coordinatrice, team di assistenza domiciliare · 22 dipendenti",
      result: "Un solo registro invece di tre",
    },
    relatedSlugs: ["wellness", "offices", "cleaning"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
];
